import React, { useState, useEffect, useCallback } from 'react';
import { API } from '../App';
import AttachmentsWidget from '../components/AttachmentsWidget';

const STAGES = [
  { key: 'initial',    label: 'Initial',    color: 'var(--text3)' },
  { key: 'kyc',        label: 'KYC',        color: '#f59e0b' },
  { key: 'loi',        label: 'LOI',        color: '#3b82f6' },
  { key: 'spa',        label: 'SPA',        color: '#8b5cf6' },
  { key: 'execution',  label: 'Execution',  color: '#ec4899' },
  { key: 'settlement', label: 'Settlement', color: '#22c55e' },
  { key: 'billing',    label: 'Billing',    color: '#10b981' },
  { key: 'completed',  label: 'Completed',  color: '#059669' },
  { key: 'cancelled',  label: 'Cancelled',  color: '#ef4444' },
];

const DOC_STATUS = [
  { key: 'pending',   label: 'Pending',   color: '#f59e0b' },
  { key: 'received',  label: 'Received',  color: '#3b82f6' },
  { key: 'approved',  label: 'Approved',  color: '#22c55e' },
  { key: 'rejected',  label: 'Rejected',  color: '#ef4444' },
];

const EMPTY_FORM = {
  reference: '', title: '', product_type: '', quantity: '', unit_price: '',
  total_value: '', currency: 'USD', buyer_name: '', seller_name: '',
  incoterm: '', port_of_loading: '', port_of_discharge: '',
  contract_date: '', laycan_start: '', laycan_end: '', eta: '',
  stage: 'initial', loi_status: 'pending', spa_status: 'pending',
  lc_status: 'pending', bl_status: 'pending',
  commission_pct: '', profit_split: '', notes: '',
};

function stageMeta(key) {
  return STAGES.find(s => s.key === key) || STAGES[0];
}

function StageBadge({ stage }) {
  const m = stageMeta(stage);
  return (
    <span style={{ display: 'inline-flex', alignItems: 'center', padding: '2px 8px',
      borderRadius: 4, fontSize: 11, fontWeight: 700,
      background: m.color + '22', color: m.color }}>
      {m.label}
    </span>
  );
}

function DocStatusSelect({ value, onChange }) {
  return (
    <select value={value} onChange={e => onChange(e.target.value)}
      style={{ fontSize: 11, padding: '2px 6px', borderRadius: 4,
        border: '1px solid var(--border)', background: 'var(--bg-surface)', color: 'var(--text1)' }}>
      {DOC_STATUS.map(s => <option key={s.key} value={s.key}>{s.label}</option>)}
    </select>
  );
}

// ── Deal Modal ─────────────────────────────────────────────────────────────────

function DealModal({ deal, onClose, onSaved }) {
  const [form, setForm] = useState(deal ? { ...deal } : { ...EMPTY_FORM });
  const [saving, setSaving] = useState(false);
  const [err, setErr] = useState('');

  const set = (k, v) => setForm(f => ({ ...f, [k]: v }));

  const handleSave = async () => {
    if (!form.title.trim()) { setErr('Titel ist erforderlich'); return; }
    setSaving(true); setErr('');
    try {
      if (deal) {
        await API.put(`/deals/${deal.id}`, form);
      } else {
        await API.post('/deals', form);
      }
      onSaved();
      onClose();
    } catch (e) {
      setErr(e.response?.data?.detail || 'Fehler beim Speichern');
    } finally { setSaving(false); }
  };

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal" style={{ maxWidth: 720 }} onClick={e => e.stopPropagation()}>
        <div className="modal-title">{deal ? 'Deal bearbeiten' : 'Neuen Deal anlegen'}</div>
        {err && <div className="error-msg">{err}</div>}

        <div className="grid-2">
          <div className="form-group">
            <label className="form-label">Referenz (auto wenn leer)</label>
            <input className="form-input" value={form.reference} onChange={e => set('reference', e.target.value)} placeholder="DEAL-0001" />
          </div>
          <div className="form-group">
            <label className="form-label">Titel *</label>
            <input className="form-input" value={form.title} onChange={e => set('title', e.target.value)} placeholder="z.B. Crude Oil Q2 2026" />
          </div>
        </div>

        <div className="grid-2">
          <div className="form-group">
            <label className="form-label">Produkt</label>
            <input className="form-input" value={form.product_type} onChange={e => set('product_type', e.target.value)} placeholder="z.B. Crude Oil D6, LNG, Coal" />
          </div>
          <div className="form-group">
            <label className="form-label">Menge</label>
            <input className="form-input" value={form.quantity} onChange={e => set('quantity', e.target.value)} placeholder="z.B. 50,000 MT" />
          </div>
        </div>

        <div className="grid-3">
          <div className="form-group">
            <label className="form-label">Einheitspreis</label>
            <input className="form-input" value={form.unit_price} onChange={e => set('unit_price', e.target.value)} placeholder="z.B. 82.50 USD/bbl" />
          </div>
          <div className="form-group">
            <label className="form-label">Gesamtwert</label>
            <input className="form-input" value={form.total_value} onChange={e => set('total_value', e.target.value)} placeholder="z.B. 4,125,000" />
          </div>
          <div className="form-group">
            <label className="form-label">Währung</label>
            <select className="form-input" value={form.currency} onChange={e => set('currency', e.target.value)}>
              {['USD', 'EUR', 'GBP', 'CHF', 'AED', 'SGD'].map(c => <option key={c}>{c}</option>)}
            </select>
          </div>
        </div>

        <div className="grid-2">
          <div className="form-group">
            <label className="form-label">Buyer</label>
            <input className="form-input" value={form.buyer_name} onChange={e => set('buyer_name', e.target.value)} />
          </div>
          <div className="form-group">
            <label className="form-label">Seller</label>
            <input className="form-input" value={form.seller_name} onChange={e => set('seller_name', e.target.value)} />
          </div>
        </div>

        <div className="grid-2">
          <div className="form-group">
            <label className="form-label">Port of Loading</label>
            <input className="form-input" value={form.port_of_loading} onChange={e => set('port_of_loading', e.target.value)} />
          </div>
          <div className="form-group">
            <label className="form-label">Port of Discharge</label>
            <input className="form-input" value={form.port_of_discharge} onChange={e => set('port_of_discharge', e.target.value)} />
          </div>
        </div>

        <div className="grid-2">
          <div className="form-group">
            <label className="form-label">Incoterm</label>
            <select className="form-input" value={form.incoterm} onChange={e => set('incoterm', e.target.value)}>
              <option value="">— wählen —</option>
              {['FOB', 'CIF', 'CFR', 'DDP', 'DAP', 'FCA', 'EXW', 'CPT', 'CIP'].map(t => <option key={t}>{t}</option>)}
            </select>
          </div>
          <div className="form-group">
            <label className="form-label">Stage</label>
            <select className="form-input" value={form.stage} onChange={e => set('stage', e.target.value)}>
              {STAGES.map(s => <option key={s.key} value={s.key}>{s.label}</option>)}
            </select>
          </div>
        </div>

        <div className="grid-3">
          <div className="form-group">
            <label className="form-label">Contract Date</label>
            <input type="date" className="form-input" value={form.contract_date} onChange={e => set('contract_date', e.target.value)} />
          </div>
          <div className="form-group">
            <label className="form-label">Laycan Start</label>
            <input type="date" className="form-input" value={form.laycan_start} onChange={e => set('laycan_start', e.target.value)} />
          </div>
          <div className="form-group">
            <label className="form-label">Laycan End</label>
            <input type="date" className="form-input" value={form.laycan_end} onChange={e => set('laycan_end', e.target.value)} />
          </div>
        </div>

        <div className="grid-2">
          <div className="form-group">
            <label className="form-label">Provision %</label>
            <input className="form-input" value={form.commission_pct} onChange={e => set('commission_pct', e.target.value)} placeholder="z.B. 2.5%" />
          </div>
          <div className="form-group">
            <label className="form-label">Profit Split</label>
            <input className="form-input" value={form.profit_split} onChange={e => set('profit_split', e.target.value)} placeholder="z.B. 40/60" />
          </div>
        </div>

        <div className="form-group">
          <label className="form-label">Notizen</label>
          <textarea className="form-input" rows={3} value={form.notes} onChange={e => set('notes', e.target.value)} />
        </div>

        <div className="modal-footer">
          <button className="btn btn-ghost" onClick={onClose}>Abbrechen</button>
          <button className="btn btn-primary" onClick={handleSave} disabled={saving}>
            {saving ? 'Speichern…' : 'Speichern'}
          </button>
        </div>
      </div>
    </div>
  );
}

// ── Detail View ────────────────────────────────────────────────────────────────

function DealDetail({ deal, onBack, onEdit, onDeleted, user }) {
  const [docStatus, setDocStatus] = useState({
    loi_status: deal.loi_status,
    spa_status: deal.spa_status,
    lc_status:  deal.lc_status,
    bl_status:  deal.bl_status,
  });
  const [saving, setSaving] = useState(false);

  const saveDocStatus = async (field, val) => {
    const updated = { ...docStatus, [field]: val };
    setDocStatus(updated);
    setSaving(true);
    try { await API.put(`/deals/${deal.id}`, { [field]: val }); }
    finally { setSaving(false); }
  };

  const PIPELINE = STAGES.filter(s => !['completed', 'cancelled'].includes(s.key));
  const stageIdx = PIPELINE.findIndex(s => s.key === deal.stage);

  return (
    <div>
      <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 20 }}>
        <button className="btn btn-ghost btn-sm" onClick={onBack}>← Zurück</button>
        <h1 style={{ fontSize: 20, fontWeight: 700 }}>{deal.reference} — {deal.title}</h1>
        <StageBadge stage={deal.stage} />
        <div style={{ marginLeft: 'auto', display: 'flex', gap: 8 }}>
          <button className="btn btn-ghost btn-sm" onClick={onEdit}>Bearbeiten</button>
          {user?.role === 'admin' && (
            <button className="btn btn-danger btn-sm" onClick={onDeleted}>Löschen</button>
          )}
        </div>
      </div>

      {/* Pipeline Progress */}
      <div className="card" style={{ marginBottom: 16 }}>
        <div className="card-title">Deal Pipeline</div>
        <div style={{ display: 'flex', gap: 0, overflowX: 'auto' }}>
          {PIPELINE.map((s, i) => {
            const active = s.key === deal.stage;
            const done = i < stageIdx;
            const color = done ? '#22c55e' : active ? s.color : 'var(--border)';
            return (
              <div key={s.key} style={{ flex: 1, minWidth: 80, textAlign: 'center', position: 'relative' }}>
                <div style={{ height: 4, background: i === 0 ? 'transparent' : done || active ? '#22c55e' : 'var(--border)', marginBottom: 8 }} />
                <div style={{ width: 28, height: 28, borderRadius: '50%', background: color,
                  margin: '0 auto 4px', display: 'flex', alignItems: 'center', justifyContent: 'center',
                  fontSize: 12, fontWeight: 700, color: done || active ? '#000' : 'var(--text3)',
                  border: active ? `2px solid ${s.color}` : 'none' }}>
                  {done ? '✓' : i + 1}
                </div>
                <div style={{ fontSize: 10, color: active ? s.color : done ? '#22c55e' : 'var(--text3)', fontWeight: active ? 700 : 400 }}>
                  {s.label}
                </div>
              </div>
            );
          })}
        </div>
      </div>

      <div className="grid-2">
        {/* Deal Info */}
        <div className="card">
          <div className="card-title">Deal Details</div>
          <table style={{ width: '100%', fontSize: 13, borderCollapse: 'collapse' }}>
            <tbody>
              {[
                ['Produkt', deal.product_type],
                ['Menge', deal.quantity],
                ['Einheitspreis', deal.unit_price],
                ['Gesamtwert', deal.total_value ? `${deal.total_value} ${deal.currency}` : ''],
                ['Buyer', deal.buyer_name],
                ['Seller', deal.seller_name],
                ['Incoterm', deal.incoterm],
                ['Port of Loading', deal.port_of_loading],
                ['Port of Discharge', deal.port_of_discharge],
                ['Contract Date', deal.contract_date],
                ['Laycan', deal.laycan_start && deal.laycan_end ? `${deal.laycan_start} – ${deal.laycan_end}` : deal.laycan_start || ''],
                ['ETA', deal.eta],
              ].map(([label, val]) => val ? (
                <tr key={label}>
                  <td style={{ padding: '5px 0', color: 'var(--text3)', width: 140 }}>{label}</td>
                  <td style={{ padding: '5px 0', color: 'var(--text1)', fontWeight: 600 }}>{val}</td>
                </tr>
              ) : null)}
            </tbody>
          </table>
        </div>

        {/* Document Checklist */}
        <div className="card">
          <div className="card-title">Dokument-Status {saving && <span style={{ fontSize: 11, color: 'var(--text3)', fontWeight: 400 }}>speichert…</span>}</div>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
            {[
              { key: 'loi_status', label: 'Letter of Intent (LOI)' },
              { key: 'spa_status', label: 'Sales & Purchase Agreement (SPA)' },
              { key: 'lc_status',  label: 'Letter of Credit (LC/SBLC)' },
              { key: 'bl_status',  label: 'Bill of Lading (BL)' },
            ].map(({ key, label }) => (
              <div key={key} style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '8px 0', borderBottom: '1px solid var(--border)' }}>
                <span style={{ fontSize: 13 }}>{label}</span>
                <DocStatusSelect value={docStatus[key]} onChange={v => saveDocStatus(key, v)} />
              </div>
            ))}
          </div>

          {/* Commission / Profit */}
          {(deal.commission_pct || deal.profit_split) && (
            <div style={{ marginTop: 16, padding: '10px 12px', background: 'var(--bg-surface)', borderRadius: 8 }}>
              {deal.commission_pct && <div style={{ fontSize: 13, marginBottom: 4 }}>
                <span style={{ color: 'var(--text3)' }}>Provision: </span>
                <span style={{ fontWeight: 700, color: 'var(--accent)' }}>{deal.commission_pct}</span>
              </div>}
              {deal.profit_split && <div style={{ fontSize: 13 }}>
                <span style={{ color: 'var(--text3)' }}>Profit Split: </span>
                <span style={{ fontWeight: 700, color: 'var(--accent)' }}>{deal.profit_split}</span>
              </div>}
            </div>
          )}
        </div>
      </div>

      {deal.notes && (
        <div className="card">
          <div className="card-title">Notizen</div>
          <p style={{ fontSize: 13, color: 'var(--text2)', lineHeight: 1.6, whiteSpace: 'pre-wrap' }}>{deal.notes}</p>
        </div>
      )}

      <div className="card">
        <div className="card-title">📎 Anhänge</div>
        <AttachmentsWidget entityType="deal" entityId={deal.id} />
      </div>
    </div>
  );
}

// ── Main Page ──────────────────────────────────────────────────────────────────

export default function DealsPage() {
  const [deals, setDeals] = useState([]);
  const [loading, setLoading] = useState(true);
  const [stageFilter, setStageFilter] = useState('');
  const [q, setQ] = useState('');
  const [view, setView] = useState('pipeline'); // 'pipeline' | 'list'
  const [selected, setSelected] = useState(null);
  const [modal, setModal] = useState(null); // null | 'create' | deal-obj
  const [user] = useState(() => {
    try { return JSON.parse(localStorage.getItem('energy_auth') || 'null')?.user; } catch { return null; }
  });

  const load = useCallback(async () => {
    try {
      const params = {};
      if (stageFilter) params.stage = stageFilter;
      if (q) params.q = q;
      const r = await API.get('/deals', { params });
      setDeals(r.data);
    } catch { /* ignore */ }
    finally { setLoading(false); }
  }, [stageFilter, q]);

  useEffect(() => { load(); }, [load]);

  const handleDelete = async () => {
    if (!selected) return;
    if (!window.confirm(`Deal "${selected.title}" wirklich löschen?`)) return;
    await API.delete(`/deals/${selected.id}`);
    setSelected(null);
    load();
  };

  // ── Detail View ─────────────────────────────────────────────────────────────
  if (selected) {
    return (
      <div className="page-content">
        {modal && (
          <DealModal deal={modal === 'edit' ? selected : null}
            onClose={() => setModal(null)}
            onSaved={async () => {
              const r = await API.get(`/deals/${selected.id}`);
              setSelected(r.data);
              load();
            }} />
        )}
        <DealDetail
          deal={selected}
          user={user}
          onBack={() => setSelected(null)}
          onEdit={() => setModal('edit')}
          onDeleted={handleDelete}
        />
      </div>
    );
  }

  // ── List / Pipeline View ────────────────────────────────────────────────────

  const activePipelineStages = STAGES.filter(s => !['completed', 'cancelled'].includes(s.key));
  const groupedByStage = Object.fromEntries(
    STAGES.map(s => [s.key, deals.filter(d => d.stage === s.key)])
  );

  return (
    <div className="page-content">
      {modal && (
        <DealModal deal={modal !== 'create' ? modal : null}
          onClose={() => setModal(null)}
          onSaved={load} />
      )}

      {/* Header */}
      <div className="page-header" style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between' }}>
        <div>
          <h1>Deal Management</h1>
          <p>Cargo-Deals & Trade-Finance-Pipeline ({deals.length} Deals)</p>
        </div>
        <button className="btn btn-primary" onClick={() => setModal('create')}>+ Neuer Deal</button>
      </div>

      {/* Toolbar */}
      <div style={{ display: 'flex', gap: 10, marginBottom: 16, flexWrap: 'wrap', alignItems: 'center' }}>
        <input className="form-input" style={{ width: 220 }} placeholder="Suche…" value={q}
          onChange={e => setQ(e.target.value)} />
        <select className="form-input" style={{ width: 160 }} value={stageFilter}
          onChange={e => setStageFilter(e.target.value)}>
          <option value="">Alle Stages</option>
          {STAGES.map(s => <option key={s.key} value={s.key}>{s.label}</option>)}
        </select>
        <div style={{ display: 'flex', gap: 4, marginLeft: 'auto' }}>
          {['pipeline', 'list'].map(v => (
            <button key={v} className={`btn btn-sm ${view === v ? 'btn-primary' : 'btn-ghost'}`}
              onClick={() => setView(v)}>
              {v === 'pipeline' ? '⬛ Pipeline' : '☰ Liste'}
            </button>
          ))}
        </div>
      </div>

      {/* Stage Stats Bar */}
      <div style={{ display: 'flex', gap: 8, marginBottom: 20, flexWrap: 'wrap' }}>
        {activePipelineStages.map(s => {
          const cnt = groupedByStage[s.key]?.length || 0;
          return (
            <button key={s.key} onClick={() => setStageFilter(stageFilter === s.key ? '' : s.key)}
              style={{ display: 'flex', alignItems: 'center', gap: 5, padding: '4px 10px',
                borderRadius: 6, border: `1px solid ${stageFilter === s.key ? s.color : 'var(--border)'}`,
                background: stageFilter === s.key ? s.color + '22' : 'var(--bg-card)',
                color: stageFilter === s.key ? s.color : 'var(--text2)',
                cursor: 'pointer', fontSize: 12, fontWeight: 600 }}>
              <span style={{ width: 8, height: 8, borderRadius: '50%', background: s.color, display: 'inline-block' }} />
              {s.label}
              <span style={{ background: s.color + '33', color: s.color, borderRadius: 10,
                padding: '0 5px', fontSize: 11 }}>{cnt}</span>
            </button>
          );
        })}
        {/* Completed + Cancelled */}
        {[STAGES.find(s => s.key === 'completed'), STAGES.find(s => s.key === 'cancelled')].map(s => {
          const cnt = groupedByStage[s.key]?.length || 0;
          if (!cnt) return null;
          return (
            <button key={s.key} onClick={() => setStageFilter(stageFilter === s.key ? '' : s.key)}
              style={{ display: 'flex', alignItems: 'center', gap: 5, padding: '4px 10px',
                borderRadius: 6, border: `1px solid ${stageFilter === s.key ? s.color : 'var(--border)'}`,
                background: stageFilter === s.key ? s.color + '22' : 'var(--bg-card)',
                color: stageFilter === s.key ? s.color : 'var(--text3)',
                cursor: 'pointer', fontSize: 12, fontWeight: 600 }}>
              {s.label} <span style={{ color: s.color }}>{cnt}</span>
            </button>
          );
        })}
      </div>

      {loading && <div className="loading">Lade Deals…</div>}
      {!loading && deals.length === 0 && (
        <div style={{ textAlign: 'center', padding: '60px 0', color: 'var(--text3)' }}>
          <div style={{ fontSize: 40, marginBottom: 12 }}>📁</div>
          <div>Noch keine Deals. Lege den ersten Deal an.</div>
        </div>
      )}

      {/* Pipeline View */}
      {!loading && deals.length > 0 && view === 'pipeline' && (
        <div style={{ display: 'flex', gap: 12, overflowX: 'auto', paddingBottom: 8 }}>
          {activePipelineStages.map(s => {
            const cards = groupedByStage[s.key] || [];
            return (
              <div key={s.key} style={{ minWidth: 220, width: 220, flexShrink: 0 }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: 6, marginBottom: 8,
                  padding: '6px 10px', borderRadius: 7,
                  background: s.color + '15', border: `1px solid ${s.color}30` }}>
                  <span style={{ width: 8, height: 8, borderRadius: '50%', background: s.color }} />
                  <span style={{ fontSize: 12, fontWeight: 700, color: s.color }}>{s.label}</span>
                  <span style={{ marginLeft: 'auto', fontSize: 11, color: s.color }}>{cards.length}</span>
                </div>
                <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
                  {cards.map(deal => (
                    <div key={deal.id} className="card" style={{ margin: 0, cursor: 'pointer',
                      borderLeft: `3px solid ${s.color}` }}
                      onClick={() => setSelected(deal)}>
                      <div style={{ fontSize: 10, color: 'var(--text3)', marginBottom: 2 }}>{deal.reference}</div>
                      <div style={{ fontSize: 13, fontWeight: 600, marginBottom: 4, lineHeight: 1.3 }}>{deal.title}</div>
                      {deal.product_type && <div style={{ fontSize: 11, color: 'var(--text3)' }}>{deal.product_type}</div>}
                      {deal.total_value && (
                        <div style={{ fontSize: 12, color: 'var(--accent)', fontWeight: 700, marginTop: 4 }}>
                          {deal.total_value} {deal.currency}
                        </div>
                      )}
                      {deal.buyer_name && <div style={{ fontSize: 11, color: 'var(--text3)', marginTop: 2 }}>B: {deal.buyer_name}</div>}
                    </div>
                  ))}
                </div>
              </div>
            );
          })}
        </div>
      )}

      {/* List View */}
      {!loading && deals.length > 0 && view === 'list' && (
        <div className="card" style={{ padding: 0 }}>
          <table className="tbl">
            <thead>
              <tr>
                <th>Ref</th>
                <th>Titel</th>
                <th>Produkt</th>
                <th>Buyer / Seller</th>
                <th>Wert</th>
                <th>Stage</th>
                <th>Erstellt</th>
                <th></th>
              </tr>
            </thead>
            <tbody>
              {deals.map(deal => (
                <tr key={deal.id} style={{ cursor: 'pointer' }} onClick={() => setSelected(deal)}>
                  <td style={{ color: 'var(--text3)', fontFamily: 'monospace', fontSize: 12 }}>{deal.reference}</td>
                  <td style={{ fontWeight: 600 }}>{deal.title}</td>
                  <td style={{ color: 'var(--text3)' }}>{deal.product_type || '—'}</td>
                  <td style={{ fontSize: 12, color: 'var(--text3)' }}>
                    {[deal.buyer_name, deal.seller_name].filter(Boolean).join(' / ') || '—'}
                  </td>
                  <td style={{ fontWeight: 700, color: 'var(--accent)' }}>
                    {deal.total_value ? `${deal.total_value} ${deal.currency}` : '—'}
                  </td>
                  <td><StageBadge stage={deal.stage} /></td>
                  <td style={{ color: 'var(--text3)', fontSize: 12 }}>
                    {deal.created_at ? new Date(deal.created_at).toLocaleDateString('de-DE') : '—'}
                  </td>
                  <td>
                    <button className="btn btn-ghost btn-sm" onClick={e => { e.stopPropagation(); setModal(deal); }}>✎</button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}
